import { z } from 'zod';

export const FEEDBACK_LIMITS = { summary: 100, details: 4000, expected: 2000 } as const;
const feedbackDeviceSchema = z
  .object({
    browser: z.enum(['Chrome', 'Safari', 'Firefox', 'Edge', 'Other']),
    browserMajor: z.number().int().min(1).max(9999).nullable(),
    platform: z.enum(['iOS', 'Android', 'macOS', 'Windows', 'Linux', 'Other']),
    width: z.number().int().min(1).max(10000),
    height: z.number().int().min(1).max(10000),
  })
  .strict();

export const feedbackRequestSchema = z
  .object({
    submissionId: z.uuid(),
    kind: z.enum(['bug', 'suggestion']),
    summary: z
      .string()
      .trim()
      .min(5)
      .max(FEEDBACK_LIMITS.summary)
      .refine((s) => !/[\r\n]/.test(s)),
    details: z.string().trim().min(10).max(FEEDBACK_LIMITS.details),
    expected: z.string().trim().max(FEEDBACK_LIMITS.expected),
    client: z
      .object({
        version: z.string().regex(/^[A-Za-z0-9.+_-]{1,32}$/),
        revision: z.string().regex(/^(?:[a-f0-9]{7,40}(?:-dirty)?|local|unknown)$/),
        builtAt: z.iso.datetime().nullable(),
        submittedAt: z.iso.datetime(),
        device: feedbackDeviceSchema.nullable(),
      })
      .strict(),
  })
  .strict();
export type FeedbackRequest = z.infer<typeof feedbackRequestSchema>;
export type FeedbackDevice = z.infer<typeof feedbackDeviceSchema>;

export const feedbackReplySchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('created'),
      issueNumber: z.number().int().positive(),
      submittedAt: z.iso.datetime(),
    })
    .strict(),
  z
    .object({
      status: z.enum([
        'pending',
        'uncertain',
        'unavailable',
        'invalid',
        'unauthorized',
        'conflict',
        'failed',
      ]),
    })
    .strict(),
  z
    .object({ status: z.enum(['retry', 'rate_limited']), retryAfterSeconds: z.number().int().positive() })
    .strict(),
]);
export type FeedbackReply = z.infer<typeof feedbackReplySchema>;
